import _, { map, uniqBy } from 'lodash';
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';
import { useGroupsFindLectureGroupsByTeacher, useTeacherGroupsFindByTeacher } from 'src/generated/endpoint';
import { Group, ResponseTeachersDto, ResponseUserDto, TeacherActivityV3InfoDto } from 'src/generated/model';
import { useLanguage } from 'src/hooks/useLanguage';
import { toastState } from 'src/store';
import { twMerge } from 'tailwind-merge';
import { Button } from '../common/Button';
import { Checkbox } from '../common/Checkbox';
import { Icon } from '../common/icons';
import { SelectMenus } from '../SelectMenus';
import { SuperModal } from '../SuperModal';

interface ActivityTeacherSelectModalProps {
  teachers: ResponseTeachersDto[];
  modalOpen: boolean;
  setModalOpen: (value: boolean) => void;
  selectedTeacherInfos: TeacherActivityV3InfoDto[];
  setSelectedTeacherInfos: Dispatch<SetStateAction<TeacherActivityV3InfoDto[]>>;
  me?: ResponseUserDto;
  groups: Group[];
}

export const ActivityTeacherSelectModal: FC<ActivityTeacherSelectModalProps> = ({
  teachers,
  modalOpen: selectTeacherModalOpen,
  setModalOpen: setSelectTeacherModalOpen,
  selectedTeacherInfos,
  setSelectedTeacherInfos,
  me,
  groups,
}) => {
  const { t } = useLanguage();
  const [toastMsg, setToastMsg] = useRecoilState(toastState);
  const [allGroupData, setAllGroupData] = useState<Group[]>([]);

  // state
  const [selectedTeacherId, setSelectedTeacherId] = useState<number | undefined>(me?.id);
  const [_selectedTeacherInfos, _setSelectedTeacherInfos] = useState<TeacherActivityV3InfoDto[]>([]);

  // api call
  const { data: teacherGroups = [] } = useTeacherGroupsFindByTeacher(selectedTeacherId || 0, {
    query: {
      enabled: !!selectedTeacherId,
      onSuccess: (data) => setAllGroupData(uniqBy(allGroupData.concat(data.map((el) => el.group)), 'id')),
    },
  });

  const { data: _lectureGroups = [] } = useGroupsFindLectureGroupsByTeacher({
    query: {
      enabled: !!selectedTeacherId,
      onSuccess: (data) => setAllGroupData(uniqBy(allGroupData.concat(data), 'id')),
    },
  });

  useEffect(() => {
    setAllGroupData(uniqBy(allGroupData.concat(groups), 'id'));
  }, [groups]);

  useEffect(() => {
    _setSelectedTeacherInfos(selectedTeacherInfos);
  }, [selectedTeacherInfos]);

  const teacherDatas = _.chain(teachers)
    .filter((t) => !_selectedTeacherInfos.map((el) => el.teacherId).includes(t.id))
    .uniqBy('id')
    .sortBy('name')
    .map((tg) => ({ id: tg.id, name: tg.name }))
    .value();

  const klassGroups = _.chain(teacherGroups)
    .filter((el) => el.group.type === 'KLASS')
    .map((el) => el.group)
    .uniqBy('id')
    .sortBy(['grade', 'klass', 'name'])
    .value();
  const klubGroups = _.chain(teacherGroups)
    .filter((el) => el.group.type === 'KLUB')
    .map((el) => el.group)
    .uniqBy('id')
    .sortBy(['grade', 'klass', 'name'])
    .value();
  const lectureGroups = _.chain(_lectureGroups).uniqBy('id').sortBy(['grade', 'klass', 'name']).value();

  const selectedGroupIds = _selectedTeacherInfos.find((info) => info.teacherId === selectedTeacherId)?.groupIds || [];

  const createTeacherInfo = (teacherId: number, groupIds?: number[]) => {
    _setSelectedTeacherInfos((prevInfos) => prevInfos.concat({ teacherId, groupIds: groupIds || [] }));
  };

  const updateTeacherInfo = (teacherId: number, groupIds: number[]) => {
    _setSelectedTeacherInfos((prevInfos) =>
      map(prevInfos, (info) => (info.teacherId === teacherId ? { ...info, groupIds } : info)),
    );
  };

  const deleteTeacherInfo = (teacherId: number) => {
    _setSelectedTeacherInfos((prevInfos) => prevInfos.filter((info) => info.teacherId !== teacherId));
  };

  const reset = () => _setSelectedTeacherInfos(selectedTeacherInfos);

  return (
    <SuperModal
      className="h-3/4 w-[960px] max-w-7xl 3xl:h-2/3"
      modalOpen={selectTeacherModalOpen}
      setModalClose={() => {
        reset();
        setSelectTeacherModalOpen(false);
      }}
    >
      <div className="flex h-full w-full flex-col space-y-4 p-10">
        <h1 className="text-xl font-bold">공유 교사 / 전달대상</h1>
        <div className="flex items-start space-x-4">
          <div className="flex w-max flex-row items-center gap-2">
            <SelectMenus
              items={[{ id: 0, name: t('select_teacher', '선생님 선택') }].concat(teacherDatas)}
              onChange={({ id }: { id: number }) => {
                setSelectedTeacherId(id);
                createTeacherInfo(id);
              }}
              value={{ id: 0 }}
            />
          </div>
          <div className="flex w-full space-x-2 overflow-x-auto">
            {_selectedTeacherInfos.map((info) => {
              const teacher = teachers.find((el) => el.id === info.teacherId);
              return (
                <div
                  className={twMerge(
                    'flex h-10 cursor-pointer items-center justify-between gap-2 space-x-2 whitespace-pre rounded-lg border border-gray-700 px-2 py-1',
                    selectedTeacherId === info.teacherId && 'border-orange-500 bg-orange-50',
                  )}
                  key={JSON.stringify(info)}
                  onClick={() => setSelectedTeacherId(info.teacherId)}
                >
                  <div>{teacher?.name}</div>
                  {info.teacherId !== me?.id && (
                    <Icon.Close
                      className="cursor-pointer rounded-full bg-zinc-100 p-1 text-zinc-400"
                      onClick={() => {
                        deleteTeacherInfo(info.teacherId);
                        console.log('info.teacherId', info.teacherId);
                        if (info.teacherId === selectedTeacherId) {
                          setSelectedTeacherId(undefined);
                        }
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* 전달대상 박스 */}
        <div className="flex h-full w-full gap-2 overflow-hidden">
          {/* 그룹 선택 박스 + 소속 그룹 선택 박스  */}
          <div className="flex w-full flex-row rounded border border-[#CCCCCC]">
            {/* 소속 그룹 선택 박스 */}
            <div className="flex w-full flex-col space-y-2 overflow-y-auto p-4">
              {!selectedTeacherId || !_selectedTeacherInfos.length ? (
                <div>선생님을 선택해주세요.</div>
              ) : (
                <>
                  {!!klassGroups.length && (
                    <div className="-mx-1 text-lg font-bold text-brandblue-1">학급소속 그룹</div>
                  )}
                  {klassGroups?.map((el) => (
                    <label
                      htmlFor={'klassgroup-' + el.id}
                      className="flex items-center justify-start space-x-2 rounded border border-[#DDDDDD] p-2 text-sm"
                      key={'klassgroup-' + el.id}
                      onClick={() =>
                        updateTeacherInfo(
                          selectedTeacherId,
                          selectedGroupIds.includes(el.id)
                            ? selectedGroupIds.filter((id) => id !== el.id)
                            : selectedGroupIds.concat(el.id),
                        )
                      }
                    >
                      <Checkbox
                        id={'klassgroup-' + el.id}
                        checked={selectedGroupIds.includes(el.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          updateTeacherInfo(
                            selectedTeacherId,
                            selectedGroupIds.includes(el.id)
                              ? selectedGroupIds.filter((id) => id !== el.id)
                              : selectedGroupIds.concat(el.id),
                          );
                        }}
                      />
                      <div>{el.name}</div>
                    </label>
                  ))}
                  {!!lectureGroups?.length && (
                    <div className="-mx-1 text-lg font-bold text-brandblue-1">강의시간표 그룹</div>
                  )}
                  {lectureGroups?.map((el) => (
                    <label
                      htmlFor={'lecturegroup-' + el.id}
                      className="flex items-center justify-start space-x-2 rounded border border-[#DDDDDD] p-2 text-sm"
                      key={'lecturegroup-' + el.id}
                      onClick={() =>
                        updateTeacherInfo(
                          selectedTeacherId,
                          selectedGroupIds.includes(el.id)
                            ? selectedGroupIds.filter((id) => id !== el.id)
                            : selectedGroupIds.concat(el.id),
                        )
                      }
                    >
                      <Checkbox
                        id={'lecturegroup-' + el.id}
                        checked={selectedGroupIds.includes(el.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          updateTeacherInfo(
                            selectedTeacherId,
                            selectedGroupIds.includes(el.id)
                              ? selectedGroupIds.filter((id) => id !== el.id)
                              : selectedGroupIds.concat(el.id),
                          );
                        }}
                      />
                      <div>{el.name}</div>
                    </label>
                  ))}
                  {!!klubGroups.length && (
                    <div className="-mx-1 text-lg font-bold text-brandblue-1">사용자정의 그룹</div>
                  )}
                  {klubGroups?.map((el) => (
                    <label
                      htmlFor={'klubgroup-' + el.id}
                      className="flex items-center justify-start space-x-2 rounded border border-[#DDDDDD] p-2 text-sm"
                      key={'klubgroup-' + el.id}
                      onClick={() =>
                        updateTeacherInfo(
                          selectedTeacherId,
                          selectedGroupIds.includes(el.id)
                            ? selectedGroupIds.filter((id) => id !== el.id)
                            : selectedGroupIds.concat(el.id),
                        )
                      }
                    >
                      <Checkbox
                        id={'klubgroup-' + el.id}
                        checked={selectedGroupIds.includes(el.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          updateTeacherInfo(
                            selectedTeacherId,
                            selectedGroupIds.includes(el.id)
                              ? selectedGroupIds.filter((id) => id !== el.id)
                              : selectedGroupIds.concat(el.id),
                          );
                        }}
                      />
                      <div>{el.name}</div>
                    </label>
                  ))}
                </>
              )}
            </div>
          </div>

          {/* 내가 선택한 그룹 박스 */}
          <div className="flex w-full min-w-[210px] max-w-[20%] flex-col rounded border border-[#777777] p-4">
            <div className="mb-2 font-bold">선택한 그룹</div>
            <div className="flex h-full w-full flex-col space-y-2 overflow-y-auto">
              {_selectedTeacherInfos.map(({ teacherId, groupIds }) => (
                <div className="pb-3" key={'teacher' + teacherId}>
                  <div>{teachers.find((el) => el.id === teacherId)?.name}</div>
                  <div className="mt-2 flex flex-col space-y-2">
                    {groupIds.map((groupId) => (
                      <div
                        className="flex w-full items-center justify-between space-x-2 rounded border border-stone-300 p-2 text-14"
                        key={'group' + groupId}
                      >
                        {allGroupData.find((el) => el.id === groupId)?.name}
                        <Icon.Close
                          className="cursor-pointer rounded-full bg-zinc-100 p-1 text-zinc-400"
                          onClick={() =>
                            updateTeacherInfo(
                              teacherId,
                              groupIds.filter((id) => id !== groupId),
                            )
                          }
                        />
                      </div>
                    ))}
                    {!groupIds.length && (
                      <div className="p-2 text-14 text-gray-500">하나 이상의 그룹을 선택해주세요.</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end space-x-2">
          <Button
            className="h-12 w-28 rounded-lg border border-neutral-500 bg-white text-lg font-semibold"
            onClick={() => {
              setSelectTeacherModalOpen(false);
              reset();
            }}
          >
            취소
          </Button>
          <Button
            className="h-12 w-28 rounded-lg bg-orange-500 text-lg font-semibold text-white disabled:bg-gray-500"
            disabled={!_selectedTeacherInfos.every((info) => !!info.groupIds.length)}
            onClick={() => {
              if (!_selectedTeacherInfos.find((info) => info.teacherId === me?.id)?.groupIds?.length) {
                setToastMsg(
                  '참여교사 선택 시 반드시 본인을 포함하여 선택해야합니다. 본인을 포함하지 않고 활동을 생성할 경우, 선택된 전달대상이 없어 학생의 제출물을 열람할 수 없습니다.',
                );
                return;
              }
              setSelectedTeacherInfos(_selectedTeacherInfos);
              setSelectTeacherModalOpen(false);
            }}
          >
            확인
          </Button>
        </div>
      </div>
    </SuperModal>
  );
};
